// The register screen's client for the Delta POS agent's loopback API (agent/src/api.ts). The agent runs on the
// register PC and listens on 127.0.0.1 only; this page talks to it directly from the browser, with the client token
// it got by pairing. The token is bound to this page's origin by the agent, so it is useless to another site.

import { desktop, isPrintPath } from "./desktop";

export const DEFAULT_AGENT_URL = "http://127.0.0.1:47110";
const KEY_URL = "delta.register.agentUrl", KEY_TOKEN = "delta.register.token";

function read(key: string): string | null { try { return localStorage.getItem(key); } catch { return null; } }
function write(key: string, v: string | null) {
  try { if (v === null) localStorage.removeItem(key); else localStorage.setItem(key, v); } catch { /* storage blocked: this page only */ }
}

/** ?agent=http://127.0.0.1:NNNN overrides the address (another port on this PC); anything not loopback is ignored. */
export function agentUrl(): string {
  const q = new URLSearchParams(location.search).get("agent");
  if (q && /^http:\/\/127\.0\.0\.1:\d{2,5}$/.test(q)) write(KEY_URL, q);
  const v = read(KEY_URL);
  return v && /^http:\/\/127\.0\.0\.1:\d{2,5}$/.test(v) ? v : DEFAULT_AGENT_URL;
}
export const savedToken = () => read(KEY_TOKEN);
/** Whether this screen can print without a dialog: inside Delta POS for Windows (built in), or paired with an agent. */
export const canDirectPrint = () => desktop() !== null || !!savedToken();

// Connecting from Setup (correction pass of 29 Oct 2026, item 8). Setup on this PC makes a one-time pairing code and
// opens <Delta>/pos/printers#agent-pair=<code>. The fragment never reaches Delta's server; it is taken off the address
// bar and out of the history at once (before sign-in, which may come first), kept for this tab only for the code's
// 5 minutes, and used only after the person allows it on Printers on this PC. The agent still checks the origin and
// that the code is unused and not expired.
const KEY_OFFER = "delta.register.pairOffer", OFFER_MS = 5 * 60_000;
export function captureAgentPairOffer() {
  const m = /^#agent-pair=([A-Za-z0-9]{5}-?[A-Za-z0-9]{5})$/.exec(location.hash);
  if (!location.hash.startsWith("#agent-pair=")) return;
  history.replaceState(history.state, "", location.pathname + location.search);
  if (!m) return;
  try { sessionStorage.setItem(KEY_OFFER, JSON.stringify({ code: m[1]!.toUpperCase(), at: Date.now() })); } catch { /* storage blocked: type the code instead */ }
}
/** A code from Setup is waiting in this tab (sign-in then opens Printers on this PC instead of the home page). */
export function agentPairOfferWaiting(): boolean {
  try { const raw = sessionStorage.getItem(KEY_OFFER); return !!raw && Date.now() - (JSON.parse(raw) as { at: number }).at < OFFER_MS; } catch { return false; }
}
export function takeAgentPairOffer(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY_OFFER);
    sessionStorage.removeItem(KEY_OFFER);
    const o = raw ? (JSON.parse(raw) as { code: string; at: number }) : null;
    return o && Date.now() - o.at < OFFER_MS ? o.code : null;
  } catch { return null; }
}
export const saveToken = (t: string | null) => write(KEY_TOKEN, t);

export interface AgentStatus {
  version: string;
  activated: boolean;
  device: { deviceId: string; code: string; name: string } | null;
  credential: { state: "none" | "ok" | "unavailable"; protection: string; problem: string | null };
  grant: { id: string; userName: string; expiresAt: string; maxSales: number; salesUsed: number } | null;
  sales: { total: number; pending: number; synced: number; failed: number };
  lastSync: { at: string; sent: number; stored: number; failed: number; stoppedAt?: { reason: string } | null } | null;
  signing: { available: boolean; reason: string };
  printing: { available: boolean; reason: string };
}
export interface TrainingLine { itemId: string; qty: string; unitPrice: string }
export interface TrainingPayload { version: 1; mode: "training"; lines: TrainingLine[] }
export interface AgentSale {
  saleId: string; deviceSeq: number; localNumber: string | null; status: "Pending" | "Synced" | "Failed" | string;
  attempts: number; lastError: string | null; reviewReasons: string[]; payload: TrainingPayload; deviceTime: string;
}

/** Status null: no answer at all (agent not running, connection cut, or the answer was lost). */
export interface AgentReply<T> { status: number | null; body: T | null; code: string | null }

export async function agentCall<T>(method: "GET" | "POST" | "PUT" | "DELETE", path: string, body?: unknown, token = savedToken(), timeoutMs = 20_000): Promise<AgentReply<T>> {
  // Inside Delta POS for Windows, printing is built in: print calls go to the app, never to an agent on this PC (so an
  // older agent that is still installed cannot print the same receipt as well).
  const d = desktop();
  if (d && isPrintPath(path)) {
    try {
      const r = await Promise.race([d.print(method, path, body), new Promise<null>((res) => setTimeout(() => res(null), timeoutMs))]);
      if (!r) return { status: null, body: null, code: null };
      return { status: r.status, body: r.status !== null && r.status >= 200 && r.status < 300 ? (r.body as T) : null, code: r.code };
    } catch { return { status: null, body: null, code: null }; }
  }
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  if (body !== undefined) headers["content-type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(agentUrl() + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), cache: "no-store", signal: AbortSignal.timeout(timeoutMs) });
  } catch {
    return { status: null, body: null, code: null };
  }
  let parsed: unknown = null;
  try { parsed = await res.json(); } catch { /* no JSON: keep null */ }
  const code = (parsed as { error?: { code?: string } } | null)?.error?.code ?? null;
  return { status: res.status, body: res.ok ? (parsed as T) : null, code };
}

// ---------------------------------------------------------------- unconfirmed sale
// The sale being saved: its id is made once, when the cashier presses Save, and kept here with its lines until the
// agent confirms it. Pressing Save again, a reload or a lost answer sends the same id and the same lines, and the
// agent stores a sale id only once, so a repeated click or a retry never creates a second sale.
//
// One record per sale, keyed by its sale id, in IndexedDB with strict durability: a write counts only once its
// transaction has completed and reads back. If a record cannot be kept (storage blocked or full), the sale is not
// sent at all. Every change names the sale it is for and happens in one transaction: a record is created only while
// no other is open, updated only if it still exists, and deleted by its own id, so a late callback for one sale can
// never overwrite or remove another sale's record, or bring back one already removed.
//
// Only the tab that owns the register (claimRegister below) reads or writes these records.

/**
 * pending: sent or about to be sent, outcome not known yet. uncertain: the agent may have saved it (no answer, or an
 * answer that does not confirm this sale). blocked: the agent refused this attempt, but an earlier one may have
 * saved it, so it stays locked. refused: the agent's answer proves it is not saved; it may be changed. conflict: the
 * agent holds a different sale under this id. saved: the agent confirmed it; only removing the record is left.
 */
export type UnconfirmedState = "pending" | "uncertain" | "blocked" | "refused" | "conflict" | "saved";
export interface UnconfirmedSale { saleId: string; payload: TrainingPayload; createdAt: string; state: UnconfirmedState; code?: string | null }

const DB_NAME = "delta-register", DB_VERSION = 2, DB_STORE = "sales";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let req: IDBOpenDBRequest;
    try { req = indexedDB.open(DB_NAME, DB_VERSION); } catch (e) { reject(e); return; }
    req.onupgradeneeded = (ev) => {
      const db = req.result;
      const st = db.createObjectStore(DB_STORE, { keyPath: "saleId" });
      // Version 1 kept one record under the key "current": carry it over under its sale id.
      if (ev.oldVersion === 1 && db.objectStoreNames.contains("unconfirmed")) {
        const old = req.transaction!.objectStore("unconfirmed");
        const g = old.get("current");
        g.onsuccess = () => { if (g.result?.saleId) st.put({ state: "uncertain", ...g.result }); db.deleteObjectStore("unconfirmed"); };
      }
    };
    req.onsuccess = () => { req.result.onversionchange = () => req.result.close(); resolve(req.result); };
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("blocked"));
  });
}

/** Runs fn in one strict read-write (or read-only) transaction; resolves with what fn decided once it committed. */
function tx<T>(mode: IDBTransactionMode, fn: (st: IDBObjectStore, done: (v: T) => void) => void): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    let result: T | undefined, set = false;
    try {
      const t = db.transaction(DB_STORE, mode, { durability: "strict" });
      t.oncomplete = () => { db.close(); if (set) resolve(result as T); else reject(new Error("no result")); };
      t.onerror = t.onabort = () => { db.close(); reject(t.error ?? new Error("aborted")); };
      fn(t.objectStore(DB_STORE), (v) => { result = v; set = true; });
    } catch (e) { db.close(); reject(e); }
  }));
}
const all = () => tx<UnconfirmedSale[]>("readonly", (st, done) => { const r = st.getAll(); r.onsuccess = () => done(r.result); });
const one = (saleId: string) => tx<UnconfirmedSale | null>("readonly", (st, done) => { const r = st.get(saleId); r.onsuccess = () => done(r.result ?? null); });

/** The kept records, oldest first, or ok: false when the browser does not let this page keep any. */
export async function loadUnconfirmed(): Promise<{ ok: true; sales: UnconfirmedSale[] } | { ok: false }> {
  try { return { ok: true, sales: (await all()).sort((a, b) => a.createdAt.localeCompare(b.createdAt)) }; } catch { return { ok: false }; }
}

/** Keeps a new sale, only if no other sale is open. "busy": another one is (nothing written). */
export async function createUnconfirmed(s: UnconfirmedSale): Promise<"ok" | "busy" | "failed"> {
  try {
    const r = await tx<"ok" | "busy">("readwrite", (st, done) => {
      const c = st.count();
      c.onsuccess = () => { if (c.result > 0) done("busy"); else { st.add(s); done("ok"); } };
    });
    if (r === "busy") return r;
    const back = await one(s.saleId);
    return back && back.state === s.state && samePayload(back.payload, s.payload) ? "ok" : "failed";
  } catch { return "failed"; }
}

/** Changes the state of this sale's record, if it still exists; its id and lines are never changed. */
export async function updateUnconfirmed(saleId: string, state: UnconfirmedState, code: string | null): Promise<"ok" | "gone" | "failed"> {
  try {
    const r = await tx<"ok" | "gone">("readwrite", (st, done) => {
      const g = st.get(saleId);
      g.onsuccess = () => { if (!g.result) done("gone"); else { st.put({ ...g.result, state, code }); done("ok"); } };
    });
    if (r === "gone") return r;
    return (await one(saleId))?.state === state ? "ok" : "failed";
  } catch { return "failed"; }
}

/** Removes this sale's record (and no other); true once it is gone. */
export async function deleteUnconfirmed(saleId: string): Promise<boolean> {
  try {
    await tx<true>("readwrite", (st, done) => { st.delete(saleId); done(true); });
    return (await one(saleId)) === null;
  } catch { return false; }
}

// ---------------------------------------------------------------- register ownership
// One tab at a time may sell and recover on this register: it holds a Web Lock for as long as it is open. The browser
// releases the lock when that tab closes, navigates away or crashes; a waiting tab then gets it, reads the records
// and reconciles them with the agent. Other tabs are view-only meanwhile.

export const REGISTER_LOCK = "delta-register-owner";
export type Ownership = "checking" | "owner" | "other-tab" | "unsupported";

/** Claims the register for this tab; onChange reports the outcome and a later takeover. Returns a release function. */
export function claimRegister(onChange: (o: Ownership) => void): () => void {
  if (!navigator.locks?.request) { onChange("unsupported"); return () => undefined; }
  let release: () => void = () => undefined;
  let stopped = false;
  const hold = (_lock: Lock | null) => new Promise<void>((r) => { release = r; if (stopped) r(); else onChange("owner"); });
  void navigator.locks.request(REGISTER_LOCK, { ifAvailable: true }, (lock) => {
    if (lock) return hold(lock);
    if (!stopped) onChange("other-tab");
    // Wait for the owner to go away, then take over.
    void navigator.locks.request(REGISTER_LOCK, (l) => hold(l)).catch(() => undefined);
    return undefined;
  }).catch(() => onChange("unsupported"));
  return () => { stopped = true; release(); };
}

const linesKey = (p: TrainingPayload | null | undefined) =>
  p && typeof p === "object" && Array.isArray(p.lines)
    ? JSON.stringify([p.version, p.mode, p.lines.map((l) => [l?.itemId, l?.qty, l?.unitPrice])])
    : null;
export const samePayload = (a: TrainingPayload | null | undefined, b: TrainingPayload | null | undefined) => linesKey(a) !== null && linesKey(a) === linesKey(b);

/** An answer that confirms exactly this sale: its id, its lines and a sequence number. Anything else proves nothing. */
export function confirmsSale(body: unknown, u: Pick<UnconfirmedSale, "saleId" | "payload">): body is AgentSale {
  const b = body as AgentSale | null;
  return !!b && typeof b === "object" && b.saleId === u.saleId && Number.isInteger(b.deviceSeq) && samePayload(b.payload, u.payload);
}

// Refusals the agent gives before it stores anything (agent/src/api.ts saveSale and the request checks). They prove
// that this request saved nothing; whether an earlier request for the same id did is another question.
const NOT_SAVED_BY_THIS_REQUEST = new Set([
  "validation", "invalid_json", "unsupported_media_type", "too_large", "unauthorized", "bad_host", "bad_origin",
  "not_activated", "credential_unavailable", "grant_required", "grant_exhausted"
]);
// Refusals that prove no sale exists under the id at all: the agent looks the id up before refusing for the grant,
// and validates the payload (the same lines every time) before anything can be stored.
const NOT_SAVED_AT_ALL = new Set(["grant_required", "grant_exhausted", "validation"]);

/**
 * What an answer to POST /sales means for the kept sale. firstAttempt: no earlier request for this id was ever sent
 * (in this page or before a reload), so a refusal of this one proves the sale is not saved.
 */
export function classifySaveAnswer(r: AgentReply<AgentSale>, u: UnconfirmedSale, firstAttempt: boolean): { kind: "saved"; sale: AgentSale; fresh: boolean } | { kind: Exclude<UnconfirmedState, "saved">; code: string | null } {
  if ((r.status === 201 || r.status === 200) && confirmsSale(r.body, u)) return { kind: "saved", sale: r.body!, fresh: r.status === 201 };
  if (r.status === 409 && r.code === "sale_conflict") return { kind: "conflict", code: r.code };
  if (r.status !== null && r.status >= 400 && r.code && NOT_SAVED_BY_THIS_REQUEST.has(r.code)) {
    return { kind: firstAttempt || NOT_SAVED_AT_ALL.has(r.code) ? "refused" : "blocked", code: r.code };
  }
  // No answer, a server error, an unknown refusal, or a success that does not confirm this sale: it may be saved.
  return { kind: "uncertain", code: r.code };
}
